import { getRuntimeConfig } from '@/config';

interface TokenResponse {
  access_token: string;
  id_token: string;
  refresh_token: string;
  expires_in: number;
  token_type: string;
}

interface TokenClaims {
  sub: string;
  email: string;
  given_name?: string;
  family_name?: string;
  'cognito:groups'?: string[];
  exp: number;
}

// ─── PKCE helpers ─────────────────────────────────────────────────────────────

function generateCodeVerifier(): string {
  const array = new Uint8Array(96);
  crypto.getRandomValues(array);
  return base64UrlEncode(array);
}

async function generateCodeChallenge(verifier: string): Promise<string> {
  const encoded = new TextEncoder().encode(verifier);
  const hash = await crypto.subtle.digest('SHA-256', encoded);
  return base64UrlEncode(new Uint8Array(hash));
}

function base64UrlEncode(data: Uint8Array): string {
  return btoa(String.fromCharCode(...data))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}

function generateState(): string {
  const array = new Uint8Array(16);
  crypto.getRandomValues(array);
  return base64UrlEncode(array);
}

// ─── Session storage keys ─────────────────────────────────────────────────────

const PKCE_VERIFIER_KEY = 'church_admin_pkce_verifier';
const OAUTH_STATE_KEY = 'church_admin_oauth_state';

// ─── Public API ───────────────────────────────────────────────────────────────

export async function redirectToLogin(): Promise<void> {
  const { cognitoDomain, userPoolClientId, appUrl } = getRuntimeConfig();

  const verifier = generateCodeVerifier();
  const challenge = await generateCodeChallenge(verifier);
  const state = generateState();

  sessionStorage.setItem(PKCE_VERIFIER_KEY, verifier);
  sessionStorage.setItem(OAUTH_STATE_KEY, state);

  const params = new URLSearchParams({
    client_id: userPoolClientId,
    response_type: 'code',
    scope: 'openid email profile',
    redirect_uri: `${appUrl}/callback`,
    code_challenge: challenge,
    code_challenge_method: 'S256',
    state,
  });

  window.location.href = `${cognitoDomain}/oauth2/authorize?${params}`;
}

export async function exchangeCodeForTokens(
  code: string,
  returnedState: string,
): Promise<TokenResponse> {
  const { cognitoDomain, userPoolClientId, appUrl } = getRuntimeConfig();

  const storedState = sessionStorage.getItem(OAUTH_STATE_KEY);
  const verifier = sessionStorage.getItem(PKCE_VERIFIER_KEY);

  // State validation prevents CSRF
  if (!storedState || storedState !== returnedState) {
    throw new Error('OAuth state mismatch — possible CSRF attack. Please try logging in again.');
  }

  if (!verifier) {
    throw new Error('PKCE verifier not found. Please try logging in again.');
  }

  sessionStorage.removeItem(PKCE_VERIFIER_KEY);
  sessionStorage.removeItem(OAUTH_STATE_KEY);

  const body = new URLSearchParams({
    grant_type: 'authorization_code',
    client_id: userPoolClientId,
    code,
    redirect_uri: `${appUrl}/callback`,
    code_verifier: verifier,
  });

  const response = await fetch(`${cognitoDomain}/oauth2/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body,
  });

  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error((data as { error?: string }).error ?? 'Token exchange failed');
  }

  return response.json() as Promise<TokenResponse>;
}

export async function refreshAccessToken(refreshToken: string): Promise<TokenResponse> {
  const { cognitoDomain, userPoolClientId } = getRuntimeConfig();

  const body = new URLSearchParams({
    grant_type: 'refresh_token',
    client_id: userPoolClientId,
    refresh_token: refreshToken,
  });

  const response = await fetch(`${cognitoDomain}/oauth2/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body,
  });

  if (!response.ok) throw new Error('Token refresh failed');
  return response.json() as Promise<TokenResponse>;
}

export function decodeTokenClaims(idToken: string): TokenClaims {
  const payload = idToken.split('.')[1];
  if (!payload) throw new Error('Invalid ID token');
  return JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/'))) as TokenClaims;
}

export function isTokenExpired(claims: TokenClaims): boolean {
  return Date.now() / 1000 > claims.exp - 60; // 60-second buffer
}

export function buildLogoutUrl(): string {
  const { cognitoDomain, userPoolClientId, appUrl } = getRuntimeConfig();
  const params = new URLSearchParams({
    client_id: userPoolClientId,
    logout_uri: appUrl,
  });
  return `${cognitoDomain}/logout?${params}`;
}
